/**
 * OnboardingFlow — V23.11
 *
 * Deux comportements en un :
 *   1. Modal "Choisissez votre mode" si onboarding_mode === null (1re connexion).
 *   2. Checklist sticky bottom-right si mode === "guided" et all_done === false.
 *
 * Self-contained : poll l'API toutes les 30s pour rafraîchir l'avancement
 * automatiquement quand le user agit dans d'autres modules.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { useTranslation } from "react-i18next";
import {
  Check, X, ChevronRight, Compass, Sparkles, Rocket, Loader2, Eye, EyeOff,
} from "lucide-react";

const STEP_PATHS = {
  doctrine: "/app/doctrine",
  annuaire: "/app/annuaire",
  chainage: "/app/chainage",
  veille: "/app/radar",
  team: "/app/settings",
};

export default function OnboardingFlow() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/workspace/onboarding/state");
      setState(r.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, [refresh]);

  if (!state) return null;

  // 1) Modal de choix au tout premier login
  if (state.needs_chooser) {
    return <ChoiceModal busy={busy} setBusy={setBusy} onChose={refresh} t={t} />;
  }

  // 2) Checklist sticky (mode 'guided', pas encore tout fait)
  const showChecklist = state.mode === "guided" && !state.all_done;
  if (!showChecklist) return null;

  if (collapsed) {
    return (
      <button
        onClick={() => setCollapsed(false)}
        className="fixed bottom-4 right-4 z-40 bg-[#378ADD] text-white text-xs px-3 py-2 rounded-full shadow-xl inline-flex items-center gap-1.5 hover:bg-[#2a6dad]"
        data-testid="onboarding-expand-btn"
      >
        <Compass className="w-3.5 h-3.5" />
        {t("onboarding.progress", { defaultValue: "Onboarding" })} · {state.completed}/{state.total}
      </button>
    );
  }

  const dismiss = async () => {
    if (!window.confirm(t("onboarding.dismiss_confirm", { defaultValue: "Masquer définitivement la checklist ? Tu pourras la réactiver depuis Settings." }))) return;
    setBusy(true);
    try {
      await api.post("/workspace/onboarding/dismiss");
      notify.success(t("onboarding.dismissed", { defaultValue: "Onboarding masqué." }));
      // V23.14.5 — Auto-bootstrap Bras Armé en arrière-plan (silencieux, idempotent)
      try {
        const r = await api.post("/workspace/bras-arme/auto-bootstrap");
        if (r.data?.accepted) {
          notify.success(t("onboarding.cercle_vertueux_started", {
            defaultValue: "Génération de votre premier cercle vertueux en cours…",
          }));
        }
      } catch {
        // best-effort, ne bloque pas la fermeture de l'onboarding
      }
      // V23.14.6 — Auto-bootstrap Cartographie + Chaînage (silencieux, idempotent)
      try {
        const r2 = await api.post("/workspace/cockpit/auto-bootstrap-context");
        if (r2.data?.created?.length) {
          const created_kinds = r2.data.created.map((c) => c.kind);
          if (created_kinds.includes("utility_map") || created_kinds.includes("chain")) {
            notify.success(t("onboarding.cockpit_context_started", {
              defaultValue: "Cartographie et chaînage initiaux créés — à enrichir dans les modules dédiés.",
            }));
          }
        }
      } catch {
        // best-effort
      }
      await refresh();
    } catch (e) { notify.error(fmtError(e)); }
    finally { setBusy(false); }
  };

  const switchFree = async () => {
    setBusy(true);
    try {
      await api.post("/workspace/onboarding/switch-to-free");
      notify.success(t("onboarding.now_free", { defaultValue: "Mode libre activé." }));
      await refresh();
    } catch (e) { notify.error(fmtError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div
      className="fixed bottom-4 right-4 z-40 w-[340px] max-w-[calc(100vw-2rem)] bg-[#0d1626] border border-[#378ADD]/40 rounded-xl shadow-2xl overflow-hidden"
      data-testid="onboarding-checklist"
    >
      <div className="px-4 py-3 bg-gradient-to-r from-[#378ADD]/20 to-transparent border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Compass className="w-4 h-4 text-[#378ADD]" />
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#378ADD] font-bold">
              {t("onboarding.overline", { defaultValue: "Onboarding guidé" })}
            </div>
            <div className="text-[10px] text-zinc-500">{state.completed}/{state.total} {t("onboarding.steps", { defaultValue: "étapes" })}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setCollapsed(true)} className="p-1 text-zinc-500 hover:text-white" title={t("onboarding.minimize", { defaultValue: "Réduire" })} data-testid="onboarding-collapse">
            <EyeOff className="w-3.5 h-3.5" />
          </button>
          <button onClick={dismiss} disabled={busy} className="p-1 text-zinc-500 hover:text-white disabled:opacity-50" title={t("onboarding.dismiss", { defaultValue: "Masquer définitivement" })} data-testid="onboarding-dismiss">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Barre de progression */}
      <div className="px-4 pt-3">
        <div className="h-1 bg-white/5 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#378ADD] to-[#1D9E75] transition-all duration-500"
            style={{ width: `${(state.completed / state.total) * 100}%` }}
          />
        </div>
      </div>

      <div className="p-3 space-y-1.5">
        {(state.steps_def || []).map((step, i) => {
          const done = state.steps?.[step.key]?.done;
          const value = state.steps?.[step.key]?.value;
          const target = state.steps?.[step.key]?.target;
          return (
            <button
              key={step.key}
              onClick={() => navigate(STEP_PATHS[step.key] || "/app/executive")}
              className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-2.5 transition ${
                done
                  ? "bg-[#1D9E75]/10 border border-[#1D9E75]/20 text-zinc-400"
                  : "bg-white/[0.02] border border-white/5 text-zinc-200 hover:border-[#378ADD]/40 hover:bg-white/[0.04]"
              }`}
              data-testid={`onboarding-step-${step.key}`}
            >
              <div className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                done ? "bg-[#1D9E75] text-white" : "bg-white/10 text-zinc-400 border border-white/10"
              }`}>
                {done ? <Check className="w-3 h-3" /> : i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-[12px] font-medium ${done ? "line-through opacity-60" : ""}`}>
                  {t(`onboarding.step.${step.key}`, { defaultValue: step.label })}
                </div>
                {!done && target > 1 && (
                  <div className="text-[10px] text-zinc-500">{value}/{target}</div>
                )}
              </div>
              {!done && <ChevronRight className="shrink-0 w-3.5 h-3.5 text-zinc-500" />}
            </button>
          );
        })}
      </div>

      <div className="px-3 pb-3 pt-1 border-t border-white/5 mt-1 flex items-center justify-between gap-2 text-[10px]">
        <button onClick={switchFree} disabled={busy} className="text-zinc-500 hover:text-white inline-flex items-center gap-1 disabled:opacity-50" data-testid="onboarding-switch-free">
          <Eye className="w-2.5 h-2.5" />
          {t("onboarding.switch_to_free", { defaultValue: "Passer en mode libre" })}
        </button>
        {state.all_done && (
          <span className="text-[#1D9E75] font-semibold inline-flex items-center gap-1">
            <Sparkles className="w-2.5 h-2.5" /> {t("onboarding.all_done", { defaultValue: "Tout est prêt !" })}
          </span>
        )}
      </div>
    </div>
  );
}

function ChoiceModal({ busy, setBusy, onChose, t }) {
  const choose = async (mode) => {
    setBusy(true);
    try {
      await api.post("/workspace/onboarding/choose", { mode });
      notify.success(
        mode === "guided"
          ? t("onboarding.guided_chosen", { defaultValue: "Onboarding guidé activé." })
          : t("onboarding.free_chosen", { defaultValue: "Mode libre activé. Bon voyage." })
      );
      await onChose();
    } catch (e) { notify.error(fmtError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0A1A2F]/90 backdrop-blur-sm flex items-center justify-center p-4" data-testid="onboarding-chooser-modal">
      <div className="w-full max-w-2xl bg-[#0d1626] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-6 py-5 bg-gradient-to-r from-[#378ADD]/10 to-transparent border-b border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-[#378ADD] font-bold">
            {t("onboarding.welcome_overline", { defaultValue: "Bienvenue dans Noam" })}
          </div>
          <h2 className="text-xl text-white font-bold mt-1">
            {t("onboarding.welcome_title", { defaultValue: "Comment souhaitez-vous démarrer ?" })}
          </h2>
          <p className="text-[12px] text-zinc-400 mt-1.5 leading-relaxed">
            {t("onboarding.welcome_sub", { defaultValue: "Vous démarrez en plan Test (environnement complet, toutes fonctionnalités). Choisissez votre rythme — vous pouvez changer à tout moment." })}
          </p>
        </div>

        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Option : Onboarding guidé */}
          <button
            onClick={() => choose("guided")}
            disabled={busy}
            className="text-left p-4 rounded-xl border-2 border-[#378ADD]/40 bg-[#378ADD]/5 hover:bg-[#378ADD]/10 hover:border-[#378ADD] transition group disabled:opacity-50"
            data-testid="onboarding-choose-guided"
          >
            <div className="flex items-center gap-2 mb-2">
              <Compass className="w-5 h-5 text-[#378ADD]" />
              <div className="text-[15px] text-white font-bold">{t("onboarding.guided_title", { defaultValue: "Onboarding guidé" })}</div>
            </div>
            <p className="text-[12px] text-zinc-400 leading-relaxed">
              {t("onboarding.guided_desc", { defaultValue: "Une checklist 5 étapes vous accompagne : doctrine, annuaire, première chaîne causale, veille active, équipe invitée. Idéal pour démarrer carré." })}
            </p>
            <div className="mt-3 text-[11px] text-[#378ADD] inline-flex items-center gap-1 font-semibold">
              {t("onboarding.guided_cta", { defaultValue: "Commencer la checklist" })}
              <ChevronRight className="w-3 h-3 group-hover:translate-x-0.5 transition" />
            </div>
          </button>

          {/* Option : Mode libre */}
          <button
            onClick={() => choose("free")}
            disabled={busy}
            className="text-left p-4 rounded-xl border-2 border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/20 transition group disabled:opacity-50"
            data-testid="onboarding-choose-free"
          >
            <div className="flex items-center gap-2 mb-2">
              <Rocket className="w-5 h-5 text-[#EF9F27]" />
              <div className="text-[15px] text-white font-bold">{t("onboarding.free_title", { defaultValue: "Test libre" })}</div>
            </div>
            <p className="text-[12px] text-zinc-400 leading-relaxed">
              {t("onboarding.free_desc", { defaultValue: "Vous explorez l'environnement comme bon vous semble, sans assistance. Vous pouvez activer la checklist plus tard depuis vos paramètres." })}
            </p>
            <div className="mt-3 text-[11px] text-[#EF9F27] inline-flex items-center gap-1 font-semibold">
              {t("onboarding.free_cta", { defaultValue: "Explorer librement" })}
              <ChevronRight className="w-3 h-3 group-hover:translate-x-0.5 transition" />
            </div>
          </button>
        </div>

        <div className="px-5 pb-4 text-[10px] text-zinc-600 text-center">
          {busy && (
            <span className="inline-flex items-center gap-1 text-zinc-400">
              <Loader2 className="w-3 h-3 animate-spin" /> {t("onboarding.applying", { defaultValue: "Application…" })}
            </span>
          )}
          {!busy && t("onboarding.changeable_note", { defaultValue: "Vous pouvez basculer entre les deux modes à tout moment." })}
        </div>
      </div>
    </div>
  );
}
